
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { AppSetting, AppSettingsTable } from "@/types/settings";
import { 
  fetchAppSettings, 
  updateAppSetting
} from "@/services/appSettingsService";
import { getFeatureFlags, updateFeatureFlag } from "@/services/featureFlagsService";
import { UseAppSettingsProps, AppSettingsState } from "@/types/appSettings";

export const useAppSettings = (): UseAppSettingsProps => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<AppSettingsState>({
    featureFlags: [],
    appSettings: [],
    isLoading: true
  });

  // Load settings from database
  const loadSettings = useCallback(async () => {
    try {
      setSettings(prev => ({ ...prev, isLoading: true }));
      
      // Load feature flags from database
      const featureFlags = await getFeatureFlags();
      
      // Load app settings from the database
      const appSettingsData = await fetchAppSettings();
      
      setSettings({
        featureFlags,
        appSettings: appSettingsData || [],
        isLoading: false
      });
    } catch (error) {
      console.error("Error loading settings:", error);
      toast({
        title: "Error",
        description: "Failed to load application settings",
        variant: "destructive"
      });
      setSettings(prev => ({ ...prev, isLoading: false }));
    }
  }, [toast]);

  // Load settings on initial page load
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Save a setting
  const saveSetting = async (setting: AppSetting) => {
    try {
      await updateAppSetting(setting.id, setting.value);
      
      // Update settings in state
      setSettings(prev => ({
        ...prev,
        appSettings: prev.appSettings.map(s => 
          s.id === setting.id ? setting : s
        )
      }));
      
      toast({
        title: "Settings Updated",
        description: `Setting "${setting.key}" has been saved.`
      });
    } catch (error) {
      console.error("Error saving setting:", error);
      toast({
        title: "Error",
        description: "Failed to save setting",
        variant: "destructive"
      });
      throw error;
    }
  };

  // Toggle a feature flag
  const toggleFeatureFlag = async (flagId: string, enabled: boolean) => {
    try {
      // Update the feature flag in the database
      await updateFeatureFlag(flagId, enabled);
      
      // Update feature flag in state
      setSettings(prev => ({
        ...prev,
        featureFlags: prev.featureFlags.map(flag => 
          flag.id === flagId ? { ...flag, enabled } : flag
        )
      }));
      
      toast({
        title: "Feature Flag Updated",
        description: `Feature has been ${enabled ? "enabled" : "disabled"}`,
      });
    } catch (error) {
      console.error("Error updating feature flag:", error);
      toast({
        title: "Error",
        description: "Failed to update feature flag",
        variant: "destructive"
      });
    }
  };

  // Refetch settings
  const refetchSettings = async () => {
    await loadSettings();
  };

  return {
    ...settings,
    saveSetting,
    toggleFeatureFlag,
    refetchSettings
  };
};
